import React , {useEffect} from "react";
import { Switch } from "@headlessui/react";
import clsx from "clsx";
import {
  toBusinessTZ,
  fmtHHMM,
  timeStrToISO,
  defaultTimezone,
} from "../../utils/datetime";
import { v4 as uuid } from "uuid";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { MMddInput } from "./SeasonalPricingEditor";
import { err, clsErr } from "../validation";
import { fromISODate, toISODate } from "../../utils/datetime";

/**
 * BusinessHours – operational hours editor with validation error display.
 *
 * Key change: ExceptionsEditor now surfaces slot‑level validation errors
 * (overlap / touching / order) in the UI, mirroring the behaviour we
 * already have for weekday definitions.
 */
export default function BusinessHours({
  value = {},
  onChange,
  timezone = defaultTimezone,
  exceptionsLimit = Infinity,
  copySources = [],
  onCopyFrom = () => {},
  errors = {},
  basePath = "",
}) {
  const tz = timezone;
  const updDay = (day, patch) =>
    onChange({ ...value, [day]: { ...value[day], ...patch } });

  const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];


  /* ensure every weekday key is present on first render */
  useEffect(() => {
    let changed = false;
    const next = { ...(value || {}) };
    DAYS.forEach((d) => {
      if (!next[d]) {
        changed = true;
        next[d] = {
          closed: false,
          slots: [
            {
              open: timeStrToISO("09:00", undefined, tz),
              close: timeStrToISO("18:00", undefined, tz),
            },
          ],
        };
      }
    });
    if (changed) onChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hoursErr = err(errors, basePath);

  return (
    <div>
      <h4 className="mb-2 font-semibold">Operational Hours</h4>

      {hoursErr && (
        <p
          className="text-xs text-red-500 mb-2"
          data-errkey={basePath}
        >
          {Array.isArray(hoursErr) ? hoursErr.join("; ") : hoursErr}
        </p>
      )}

      {/* weekday editor */}
      <div className="space-y-4">
        {DAYS.map((d) => {
          const def = value[d] || {};
          const day = {
            closed: def.closed || false,
            slots:
              def.slots?.length > 0
                ? def.slots
                : [
                    {
                      open: timeStrToISO("09:00", undefined, tz),
                      close: timeStrToISO("18:00", undefined, tz),
                    },
                  ],
          };

          const updateSlot = (i, p) =>
            updDay(d, {
              slots: day.slots.map((s, j) => (j === i ? { ...s, ...p } : s)),
            });

          const addSlot = () =>
            day.slots.length < 5 &&
            updDay(d, {
              slots: [
                ...day.slots,
                {
                  open: timeStrToISO("09:00", undefined, tz),
                  close: timeStrToISO("13:00", undefined, tz),
                },
              ],
            });

          const removeSlot = (i) =>
            updDay(d, { slots: day.slots.filter((_, j) => j !== i) });

          const slotErrClass = hoursErr ? "border-red-500" : "";

          return (
            <div key={d} className="text-sm">
              <div className="flex items-center justify-between">
                <span className="font-medium">{d}</span>
                <Switch
                  checked={day.closed}
                  onChange={(c) => {
                    if (c) {
                      updDay(d, { closed: true, slots: [] });
                    } else {
                      const existing = value[d]?.slots;
                      updDay(d, {
                        closed: false,
                        slots: existing?.length
                          ? existing
                          : [
                              {
                                open: timeStrToISO("09:00", undefined, tz),
                                close: timeStrToISO("18:00", undefined, tz),
                              },
                            ],
                      });
                    }
                  }}
                  className={clsx(
                    day.closed ? "bg-gray-400" : "bg-blue-600",
                    "relative inline-flex h-5 w-10 items-center rounded-full"
                  )}
                >
                  <span
                    className={clsx(
                      day.closed ? "translate-x-5" : "translate-x-1",
                      "inline-block h-3 w-3 transform rounded-full bg-white transition"
                    )}
                  />
                </Switch>
              </div>

              {!day.closed && (
                <div className="space-y-2 mt-2 ml-4">
                  {day.slots.map((slot, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        type="time"
                        value={fmtHHMM(toBusinessTZ(slot.open, tz))}
                        onChange={(e) =>
                          updateSlot(i, {
                            open: timeStrToISO(e.target.value, null, tz),
                          })
                        }
                        className={`rounded border-gray-300 ${slotErrClass}`}
                      />
                      <span>–</span>
                      <input
                        type="time"
                        value={fmtHHMM(toBusinessTZ(slot.close, tz))}
                        onChange={(e) =>
                          updateSlot(i, {
                            close: timeStrToISO(e.target.value, null, tz),
                          })
                        }
                        className={`rounded border-gray-300 ${slotErrClass}`}
                      />
                      {day.slots.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeSlot(i)}
                          className="text-red-500 text-sm"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}

                  {day.slots.length < 5 && (
                    <button
                      type="button"
                      onClick={addSlot}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      + Add time range
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* exceptions */}
      <ExceptionsEditor
        value={value.exceptions || []}
        onChange={(v) => onChange({ ...(value || {}), exceptions: v })}
        timezone={tz}
        limit={exceptionsLimit}
        copySources={copySources}
        onCopyFrom={onCopyFrom}
        errors={errors}
        basePath={basePath.replace(/\.hours$/, "")}
      />
    </div>
  );
}

/* ------------------------------------------------------------- */
function ExceptionsEditor({
  value,
  onChange,
  timezone = defaultTimezone,
  limit = Infinity,
  copySources = [],
  onCopyFrom = () => {},
  errors = {},
  basePath = "",
}) {
  const tz = timezone;

  const add = () =>
    onChange([
      ...value,
      {
        id: uuid(),
        date: "",
        slots: [
          {
            open: timeStrToISO("09:00", undefined, tz),
            close: timeStrToISO("18:00", undefined, tz),
          },
        ],
        closed: false,
      },
    ]);

  const upd = (id, patch) =>
    onChange(value.map((e) => (e.id === id ? { ...e, ...patch } : e)));

  const del = (id) => onChange(value.filter((e) => e.id !== id));

  const updSlot = (exId, slotIdx, patch) => {
    onChange(
      value.map((e) => {
        if (e.id !== exId) return e;
        const slots = e.slots.map((s, j) =>
          j === slotIdx ? { ...s, ...patch } : s
        );
        return { ...e, slots };
      })
    );
  };

  return (
    <div className="mt-4 space-y-2">
      <h5 className="font-medium text-sm">Black‑out / Special Dates</h5>
      <div className="text-xs text-gray-500">{value.length}/{Number.isFinite(limit) ? limit : '∞'} max</div>

      {value.map((ex, idx) => {
        const slotErr = err(errors, `${basePath}.exception${idx}.slots`);
        const slotErrClass = slotErr ? "border-red-500" : "";

        return (
          <div key={ex.id} className="space-y-1 border-b pb-2 mb-2">
            <div className="grid grid-cols-8 gap-2 items-center text-sm">
              {/* Date input (2 columns) */}
              <div className="col-span-3">
                <DatePicker
                  selected={fromISODate(ex.date)}
                  onChange={(date) =>
                    upd(ex.id, { date: toISODate(date) })
                  }
                  dateFormat="MM-dd"
                  placeholderText="MM-DD"
customInput={
  <MMddInput
    className="border rounded px-3 py-2 w-full"
    onDateSelect={(iso) => {
      const year = new Date().getFullYear();
      const full = iso.length === 5 ? `${year}-${iso}` : iso; // Convert "MM-DD" → "YYYY-MM-DD"
      upd(ex.id, { date: full });
    }}
  />
}
                />
                {err(errors, `${basePath}.exception${idx}.date`) && (
                  <p className="text-xs text-red-500 mt-1">
                    {err(errors, `${basePath}.exception${idx}.date`)}
                  </p>
                )}
              </div>

              {/* Time slots (2 columns each) */}
              {!ex.closed && (
                <>
                  <input
                    type="time"
                    value={fmtHHMM(toBusinessTZ(ex.slots[0].open, tz))}
                    onChange={(e) =>
                      updSlot(ex.id, 0, {
                        open: timeStrToISO(e.target.value, null, tz),
                      })
                    }
                    className={`col-span-2 rounded border-gray-300 ${slotErrClass}`}
                  />
                  <input
                    type="time"
                    value={fmtHHMM(toBusinessTZ(ex.slots[0].close, tz))}
                    onChange={(e) =>
                      updSlot(ex.id, 0, {
                        close: timeStrToISO(e.target.value, null, tz),
                      })
                    }
                    className={`col-span-2 rounded border-gray-300 ${slotErrClass}`}
                  />
                </>
              )}

              {/* Delete button (1 column) */}
              <button
                onClick={() => del(ex.id)}
                className="text-red-500 col-span-1"
              >
                ✕
              </button>
            </div>
            {/* inline slot‑level error (overlap / touching / order) */}
            {slotErr && (
              <p className="text-xs text-red-500 ml-8">
                {Array.isArray(slotErr) ? slotErr.join("; ") : slotErr}
              </p>
            )}

            {/* additional slots (beyond the first) */}
            {!ex.closed &&
              ex.slots.slice(1).map((s, si) => (
                <div key={si} className="flex items-center gap-2 ml-8">
                  <input
                    type="time"
                    value={fmtHHMM(toBusinessTZ(s.open, tz))}
                    onChange={(e) =>
                      updSlot(ex.id, si + 1, {
                        open: timeStrToISO(e.target.value, null, tz),
                      })
                    }
                    className={`rounded border-gray-300 ${slotErrClass}`}
                  />
                  <span>–</span>
                  <input
                    type="time"
                    value={fmtHHMM(toBusinessTZ(s.close, tz))}
                    onChange={(e) =>
                      updSlot(ex.id, si + 1, {
                        close: timeStrToISO(e.target.value, null, tz),
                      })
                    }
                    className={`rounded border-gray-300 ${slotErrClass}`}
                  />
                  <button
                    onClick={() => {
                      const slots = ex.slots.filter((_, j) => j !== si + 1);
                      upd(ex.id, { slots });
                    }}
                    className="text-red-500 text-sm"
                  >
                    ✕
                  </button>
                </div>
              ))}

            {/* controls */}
            {!ex.closed && ex.slots.length < 5 && (
              <button
                type="button"
                onClick={() =>
                  upd(ex.id, {
                    slots: [
                      ...ex.slots,
                      {
                        open: timeStrToISO("09:00", undefined, tz),
                        close: timeStrToISO("13:00", undefined, tz),
                      },
                    ],
                  })
                }
                className="text-xs text-blue-600 hover:underline ml-8"
              >
                + Add time range
              </button>
            )}

            <label className="flex items-center gap-1 text-xs ml-8">
              <input
                type="checkbox"
                checked={ex.closed}
                onChange={(e) =>
                  upd(ex.id, {
                    closed: e.target.checked,
                    slots: e.target.checked
                      ? []
                      : ex.slots?.length
                      ? ex.slots
                      : [
                          {
                            open: timeStrToISO("09:00", undefined, tz),
                            close: timeStrToISO("18:00", undefined, tz),
                          },
                        ],
                  })
                }
              />
              Closed
            </label>
          </div>
        );
      })}

      {/* add exception */}
      <button onClick={add} disabled={value.length >= limit} className={`text-xs hover:underline ${value.length >= limit ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600'}`}>
        + Add exception
      </button>

      {/* copy helper */}
      {copySources.length > 0 && (
        <div className="text-xs mt-1">
          <label>
            Copy from:&nbsp;
            <select
              defaultValue=""
              onChange={(e) => {
                if (!e.target.value) return;
                onCopyFrom(e.target.value);
                e.target.value = "";
              }}
            >
              <option value="" disabled>
                select rule
              </option>
              {copySources.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label || "Untitled"}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
    </div>
  );
}
